import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { uiText } from "@/lib/i18n/uiText";
import { getMatchStats, type StatPair } from "@/lib/matchStats.functions";

const ROWS: [string, string][] = [
  ["shots", "Shots"],
  ["shots_on_target", "Shots on target"],
  ["yellow_cards", "Yellow cards"],
  ["red_cards", "Red cards"],
  ["saves", "Goalkeeper saves"],
  ["corners", "Corners"],
  ["first_half_corners", "1st-half corners"],
  ["fouls", "Fouls"],
  ["offsides", "Offsides"],
];

/** Shots, cards, saves and set-piece signals for a pick, with their source. */
export function MatchStatsBlock({ matchId, home, away }: { matchId?: string | undefined; home: string; away: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["match-stats", matchId ?? null, home, away],
    queryFn: () => getMatchStats({ data: matchId ? { matchId, home, away } : { home, away } }),
    staleTime: 10 * 60_000,
  });

  const fmt = (v: number | null) => (v == null ? uiText("Not available yet") : String(v));

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3" aria-labelledby="match-stats-heading">
      <div className="flex items-center justify-between gap-2">
        <h3 id="match-stats-heading" className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <BarChart3 className="h-4 w-4 text-primary" aria-hidden="true" />
          {uiText("Match stats")}
        </h3>
        {data && data.mode !== "none" && (
          <span className="text-xs text-muted-foreground">
            {data.mode === "played" ? uiText("Final match stats") : uiText("Average per game, last 5 matches")}
            {data.source && data.mode === "played" ? ` · ${uiText("Source")}: ${data.source}` : ""}
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{uiText("Loading match stats…")}</p>
      ) : !data || data.mode === "none" ? (
        <p className="text-sm text-muted-foreground">
          {uiText("Shots, cards, saves and set-piece stats are not available yet for these teams. They appear here as soon as they are collected.")}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th scope="col" className="text-left font-medium py-1">{uiText("Stat")}</th>
              <th scope="col" className="text-right font-medium py-1 truncate max-w-[8rem]">{home}</th>
              <th scope="col" className="text-right font-medium py-1 truncate max-w-[8rem]">{away}</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([key, label]) => {
              const p: StatPair = data.stats[key] ?? { home: null, away: null };
              return (
                <tr key={key} className="border-t border-border/60">
                  <th scope="row" className="text-left font-normal py-1.5 text-foreground">{uiText(label)}</th>
                  <td className={`text-right py-1.5 ${p.home == null ? "text-muted-foreground text-xs" : "font-medium"}`}>{fmt(p.home)}</td>
                  <td className={`text-right py-1.5 ${p.away == null ? "text-muted-foreground text-xs" : "font-medium"}`}>{fmt(p.away)}</td>
                </tr>
              );
            })}
            <tr className="border-t border-border/60">
              <th scope="row" className="text-left font-normal py-1.5 text-foreground">{uiText("Set pieces")}</th>
              <td colSpan={2} className="text-right py-1.5 text-xs text-muted-foreground">
                {uiText("Pending — shown through corners and fouls until a data source provides set-piece counts")}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
}
